<template>
    <v-card class="my-2" variant="outlined">
        <div class="d-flex wrapper">
            <v-img
                tile
                :src="$filters.imageUrl(mainImg, 200)"
                :alt="offer.info?.name || 'tour'"
                width="200"
                max-width="200"
                height="200"
                lazy-src="/placeholder.png"
                eager
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row
                        v-show="!imgError && offer && offer.mainImageUrl"
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                    >
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div class="d-flex flex-column ma-2 flex-grow-1 flex-shrink-1 text-truncate" style="position: relative">
                <div>
                    <div class="ms-1 mb-2 font-weight-medium text-truncate text-primary">
                        <nuxt-link
                            v-if="$route.name === 'tourSearch'"
                            :to="{name: 'tour', query}"
                            no-prefetch
                            :title="offer.info.name"
                            class="text-decoration-none"
                        >
                            {{ offer.info.name }}
                        </nuxt-link>
                        <span v-else :title="offer.info.name">
                            {{ offer.info.name }}
                        </span>
                        <supplier-name-label v-if="isTO1" :supplier-name="offer.info.supplierName" class="mb-2" />
                    </div>
                    <tour-included-entries :entries="offer.entries" class="mb-1" />
                    <div class="mb-3 d-flex align-center">
                        <span class="mr-4 font-weight-medium">
                            <v-icon color="blue">mdi-clock-outline</v-icon>
                            {{
                                `${offer.info.packageDuration} ${$t('day', offer.info.packageDuration)} / ${
                                    offer.info.nightsDuration
                                } ${$t('night', offer.info.nightsDuration)}`
                            }}
                        </span>
                    </div>
                    <div v-if="offer.info.description" class="mb-3 tour-description" v-html="offer.info.description" />
                    <v-divider class="mb-3" />
                    <div class="d-flex">
                        <tour-available-date-label
                            :group-type="groupType"
                            :offers="offers"
                            :selected-date="selectedDate"
                        />
                        <tour-date-filter
                            v-if="groupType === 'tourName' && packageDates.length > 1"
                            v-model="selectedDate"
                            :offers="offers"
                        />
                    </div>
                </div>
            </div>
            <div class="ma-4 price-col flex-shrink-0 text-right">
                <template v-if="showPriceDuringSearch">
                    <div data-cy="client-price" class="font-weight-medium">
                        {{ $filters.price(totalPrice) }}
                    </div>
                    <discount-label :price="offer.price" />
                </template>
                <div class="text-caption text--secondary">
                    {{
                        `${touristsTotal} ${$t('guest', touristsTotal)}, ${offer.info.nightsDuration}
                            ${$t('night', offer.info.nightsDuration)}`
                    }}
                </div>
                <v-btn variant="flat" block color="primary" class="my-2" :to="{name: 'tour', query}">
                    {{ $t('select') }}
                </v-btn>
                <div class="text-primary cursor-pointer text-no-wrap" @click="expanded = !expanded">
                    <span>
                        {{ $t('tours.detailed_info') }}
                    </span>
                    <v-icon color="primary">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </div>
                <div
                    v-if="offers?.length > 1"
                    class="text-primary cursor-pointer text-no-wrap"
                    @click="isExpandOffers = !isExpandOffers"
                >
                    <span>
                        {{ $t('view_offers') }}
                    </span>
                    <v-icon color="primary">
                        {{ isExpandOffers ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </div>
            </div>
        </div>
        <v-expand-transition>
            <tour-offer-entries-list v-if="expanded" :offer="offer" class="pa-4" />
        </v-expand-transition>
        <v-expand-transition>
            <div v-if="isExpandOffers && expandedOffers">
                <TourOfferSubCard
                    v-for="offer in expandedOffers"
                    :key="offer.id"
                    :offer="offer"
                    :offer-data="offer.info.packageDate"
                    :offer-tariff="offer.info.offerInfo.tariffName"
                    :title="offer.info?.serviceName || offer.info?.name"
                    :secondary-service-name="offer.info?.secondaryServiceName"
                    :search-request="searchRequest"
                    @select="selectForBooking"
                />
            </div>
        </v-expand-transition>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import TourIncludedEntries from '~src/components/tours/snippets/TourIncludedEntries.vue'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import TourDateFilter from '@/components/tours/snippets/TourDateFilter.vue'
    import TourAvailableDateLabel from '@/components/tours/snippets/TourAvailableDateLabel.vue'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'
    import TourOfferEntry from '~src/components/tours/search/offers/tourOfferEntry.src'
    import TourOfferEntriesList from '@/components/tours/search/offers/TourOfferEntriesList.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import TourOfferSubCard from '~/components/tours/search/offers/TourOfferSubCard.vue'

    @Component({
        components: {
            TourOfferSubCard,
            TourOfferEntriesList,
            TourIncludedEntries,
            DiscountLabel,
            TourDateFilter,
            TourAvailableDateLabel,
            HotelStars,
            TourOfferEntry,
            SupplierNameLabel,
        },
    })
    export class TourOfferCard extends Vue {
        @Prop({required: true}) offers
        @Prop() groupType
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        imgError = false
        expanded = false
        isExpandOffers = false
        selectedDate = null

        created() {
            this.selectedDate = this.$dateFns.format(this.offers[0].info.packageDate)
        }

        get expandedOffers() {
            if (this.offers.length > 1) {
                return this.offers
            }
            return null
        }

        async selectForBooking(val) {
            const offerKey = val.offerKey
            await toursStore.ADD_TO_BASKET(offerKey)
            await this.$router.push({
                name: 'tourBooking',
                query: {offerKey},
            })
        }

        onImgError() {
            this.imgError = true
            return false
        }

        get query() {
            return {...this.searchRequest, packageTourId: this.offer.info.id, startDateFrom: this.selectedDate}
        }

        get offer() {
            if (this.groupType === 'tourName' && this.offers.length > 1) {
                return (
                    this.offers?.find(offer => this.selectedDate === this.$dateFns.format(offer.info.packageDate)) || {}
                )
            }
            return this.offers[0] || {}
        }

        get totalPrice() {
            return toursRuntimeStore.totalPrice(this.offer)
        }

        get mainImg() {
            return this.offer?.info?.mainImageURL
        }

        get searchRequest() {
            return toursStore.searchRequest
        }

        get touristsTotal() {
            return toursRuntimeStore.touristsTotal(this.searchRequest)
        }

        get packageDates() {
            return this.offers.reduce((dates, offer) => {
                dates.push(this.$dateFns.format(offer.info.packageDate))

                return dates
            }, [])
        }

        get isTO1() {
            return authStore.isTO1
        }
    }

    export default TourOfferCard
</script>

<style lang="scss" scoped>
    :deep(.tour-description p) {
        margin-bottom: 0;
    }

    .price-col {
        width: 180px;
    }

    //TODO Remove after layout redesign
    @media (max-width: 1440px) {
        .price-col {
            width: 130px;
        }
    }
</style>
